import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch,
} from "react";
import useSyncedReducer from "@core/hooks/useSyncedReducer";

type MakeContextOptions<S, A> = {
  name: string;
  initialState: S;
  reducer: (state: S, action: A) => S;
  storageKey?: string;
  syncedKeys?: (keyof S)[];
};

export function makeContext<S, A extends { type: string; payload?: any }>(
  options: MakeContextOptions<S, A>
) {
  const { name, initialState, reducer, storageKey, syncedKeys } =
    options;

  const StateContext = createContext<S | null>(null);
  const DispatchContext = createContext<Dispatch<A> | null>(null);

  function Provider({ children }: { children: ReactNode }) {
    const [state, dispatch] =
      storageKey || syncedKeys
        ? useSyncedReducer(
            reducer as any,
            initialState as any,
            {
              storageKey: storageKey ?? "__context",
              syncedKeys,
            }
          )
        : useReducer(reducer, initialState);

    return (
      <StateContext.Provider value={state}>
        <DispatchContext.Provider value={dispatch}>
          {children}
        </DispatchContext.Provider>
      </StateContext.Provider>
    );
  }

  function useStateContext(): S {
    const context = useContext(StateContext);
    if (!context) {
      throw new Error(`use${name} must be used within ${name}Provider`);
    }
    return context;
  }

  function useDispatchContext(): Dispatch<A> {
    const context = useContext(DispatchContext);
    if (!context) {
      throw new Error(`use${name}Dispatch must be used within ${name}Provider`);
    }
    return context;
  }

  return {
    Provider,
    useState: useStateContext,
    useDispatch: useDispatchContext,
  };
}

export default makeContext;

